import { useState } from 'react';
import { allExpanded, defaultStyles, JsonView } from 'react-json-view-lite';
import { Trans } from '@lingui/react/macro';

import FoldableSection from '@/design_system/FoldableSection';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useOrganizationWorkflows } from '@/models/organization';
import { WorkflowPublic } from '@/models/workflow';
import { useCurrentOrganization } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';

import './Workflows.css';
import 'react-json-view-lite/dist/index.css';

const { block, element } = createBEMClasses('workflows');

export const Workflows = () => {
  const [currentOrganization] = useCurrentOrganization();
  const { data: workflows } = useOrganizationWorkflows(currentOrganization?.slug);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowPublic | null>(null);

  const options = workflows ?? [];

  return (
    <Stack gap="16px" className={block()}>
      <Title size="200" as="h2">
        <Stack row justifyContent="space-between" alignItems="center">
          <Trans id="settings.workflows.title">Workflows</Trans>
        </Stack>
      </Title>

      <Stack row>
        <InputSelect
          variant="select"
          label={
            <Trans id="settings.workflows.select">
              Select a workflow to see its configuration and nodes
            </Trans>
          }
          value={selectedWorkflow}
          options={options}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) =>
            option.storeTypes.length
              ? `${option.type} (${option.storeTypes.join(', ')})`
              : option.type
          }
          onChange={setSelectedWorkflow}
        />
      </Stack>

      {!!selectedWorkflow && (
        <>
          <FoldableSection
            label={
              <Title size="300" as="h3">
                <Trans id="settings.workflows.section.configuration.title">Configuration</Trans>
              </Title>
            }
          >
            <JsonView
              data={selectedWorkflow.config}
              shouldExpandNode={allExpanded}
              style={{
                ...defaultStyles,
                container: element('container'),
              }}
              clickToExpandNode
            />
          </FoldableSection>

          <FoldableSection
            label={
              <Title size="300" as="h3">
                <Trans id="settings.workflows.section.nodes.title">Nodes</Trans>
              </Title>
            }
          >
            <JsonView
              data={selectedWorkflow.nodes}
              shouldExpandNode={allExpanded}
              style={{
                ...defaultStyles,
                container: element('container'),
              }}
              clickToExpandNode
            />
          </FoldableSection>
        </>
      )}
    </Stack>
  );
};
